import multiguard from 'vue-router-multiguard';
import SuppliersList from './Suppliers/SuppliersList';
import ProductView from './Products/ProductViewItem';
import { ifAuthenticated, hasAccess, isActive, hasDistributorAccess, ROLES_ACCESS, PLAN_TOOLS } from '../utils';
import { account } from '../../routes.account';
import { reportsRoutes } from './Reports/routes';
import { mergeGuards, redirectToLastTab } from '../../utils/router-helper';

const appRoutes = [
  {
    path: '/',
    name: 'app/index',
    component: () => import('@/portals/Application/Index'),
    beforeEnter: multiguard([ifAuthenticated, isActive, hasDistributorAccess, hasAccess]),
    meta: {
      id: ROLES_ACCESS.APPLICATION
    },
    children: [
      {
        path: '/forum',
        meta: { requiredTools: [PLAN_TOOLS.FORUM[0].value] },
        name: 'forum',
        component: () => import('@/portals/Application/Forum/ForumHome')
      },
      {
        path: '/forum/new',
        meta: { requiredTools: [PLAN_TOOLS.FORUM[0].value] },
        name: 'forum-new',
        component: () => import('@/portals/Application/Forum/PostComposer')
      },
      {
        path: '/forum/messages',
        meta: { requiredTools: [PLAN_TOOLS.FORUM[0].value] },
        name: 'forum-messages',
        component: () => import('@/portals/Application/Forum/ForumInbox')
      },
      {
        path: '/forum/messages/:id',
        meta: { requiredTools: [PLAN_TOOLS.FORUM[0].value] },
        name: 'forum-conversation',
        component: () => import('@/portals/Application/Forum/ForumConversation')
      },
      {
        path: '/forum/:id',
        meta: { requiredTools: [PLAN_TOOLS.FORUM[0].value] },
        name: 'forum-post',
        component: () => import('@/portals/Application/Forum/PostDetail')
      },
      {
        path: '/accounting',
        meta: { requiredTools: [PLAN_TOOLS.RECONCILLIATION[0].value] },
        name: 'accounting',
        component: () => import('@/portals/Application/Accounting')
      },
      {
        path: '/reconcile-shipments',
        redirect: '/accounting'
      },
      {
        path: '/dashboard',
        meta: { requiredTools: [PLAN_TOOLS.DASHBOARD[0].value] },
        name: 'dashboard',
        component: () => import('@/portals/Application/Dashboard/index')
      },
      {
        path: '/dashboard-gallery',
        name: 'dashboard-gallery',
        component: () => import('@/portals/Application/Dashboard/DashboardGallery'),
        beforeEnter: (to, from, next) => (process.env.NODE_ENV === 'production' ? next('/dashboard') : next())
      },
      {
        path: '/sales',
        name: 'app/sales',
        component: () => import('@/portals/Application/Sales/index')
      },
      {
        path: '/orders',
        name: 'app/orders',
        redirect: 'orders/',
        component: () => import(/* webpackChunkName: "orders" */ '@/portals/Application/Orders/List'),
        beforeEnter: redirectToLastTab('lastOrdersTab', '/orders'),
        meta: { storeLastTab: 'lastOrdersTab' },
        children: [
          {
            path: '',
            meta: { requiredTools: [PLAN_TOOLS.ORDER[0].value] },
            name: 'app/orders/',
            component: () =>
              import(/* webpackChunkName: "orders" */ '@/portals/Application/Orders/components/FollowUpsList')
          },
          {
            path: 'all',
            name: 'app/orders/all',
            component: () =>
              import(/* webpackChunkName: "orders" */ '@/portals/Application/Orders/components/OrdersList')
          },
          {
            path: 'board',
            name: 'app/orders/board',
            component: () =>
              import(/* webpackChunkName: "orders" */ '@/portals/Application/Orders/components/Board/BoardList')
          },
          {
            path: 'production',
            name: 'app/orders/production',
            component: () => import(/* webpackChunkName: "orders" */ '@/portals/Supplier/PurchaseOrders/Calendar')
          },
          {
            path: 'pos',
            name: 'app/orders/pos',
            component: () => import(/* webpackChunkName: "orders" */ '@/portals/Supplier/PurchaseOrders/List')
          },
          {
            path: 'recent',
            name: 'app/orders/recent',
            component: () =>
              import(/* webpackChunkName: "orders" */ '@/portals/Application/Orders/components/RecentOrdersList')
          },
          {
            path: 'inbox',
            name: 'app/orders/inbox',
            component: () =>
              import(/* webpackChunkName: "orders" */ '@/portals/Application/Orders/components/InboxList')
          }
        ]
      },
      {
        path: '/orders/:orderId/purchase-orders/create',
        component: () => import('@/portals/Application/Orders/PurchaseOrderFormTemplate')
      },
      {
        path: '/orders/:orderId/duplicate',
        component: () => import('@/portals/Application/Orders/OrderDuplicateForm')
      },
      {
        path: '/orders/:orderId/purchase-orders/:friendlyPurchaseOrderDetailId',
        name: 'app/orders/:orderId/purchase-orders/:friendlyPurchaseOrderDetailId',
        component: () => import('@/portals/Application/Orders/PurchaseOrderFormTemplate')
      },
      {
        path: '/orders/:orderId/purchase-orders/:friendlyPurchaseOrderDetailId/duplicate',
        component: () => import('@/portals/Application/Orders/PurchaseOrderFormTemplate')
      },
      {
        path: '/orders/:orderId/sendpurchase/:purchaseOrderId',
        name: 'app/orders/sendpurchase/:orderId/:purchaseOrderId',
        component: () => import('@/portals/Application/Orders/SendPurchaseOrder')
      },
      {
        path: '/orders/:orderId/sendinvoice',
        name: 'app/orders/:orderId/sendinvoice',
        component: () => import(/* webpackChunkName: "orders" */ '@/portals/Application/Orders/SendCustomerInvoice')
      },
      {
        path: '/orders/:orderId/sendquote',
        name: 'app/orders/:orderId/sendquote',
        component: () => import(/* webpackChunkName: "orders" */ '@/portals/Application/Orders/SendCustomerQuote')
      },
      {
        path: '/orders/:orderId/sendreceipt',
        name: 'app/orders/:orderId/sendreceipt',
        component: () => import(/* webpackChunkName: "orders" */ '@/portals/Application/Orders/SendOrderReceipt')
      },
      {
        path: '/orders/:orderId/send-presentation',
        name: 'app/orders/:orderId/send-presentation',
        component: () => import('@/portals/Application/Orders/SendPresentation')
      },
      {
        path: '/customers',
        name: 'app/customers',
        redirect: 'customers/',
        component: () => import('@/portals/Application/Customers/CustomerList'),
        beforeEnter: redirectToLastTab('lastCustomersTab', '/customers'),
        meta: { storeLastTab: 'lastCustomersTab' },
        children: [
          {
            path: '',
            name: 'app/customers/',
            meta: { requiredTools: ['follow-ups-tab'] },
            component: () => import('@/portals/Application/Customers/CustomerFollowUps')
          },
          {
            path: '/customers/all',
            name: 'app/customers/all',
            meta: { requiredTools: ['customers-tab'] },
            component: () => import('@/portals/Application/Customers/components/CustomersList')
          },
          {
            path: '/customers/companies',
            name: 'app/customers/companies',
            meta: { requiredTools: ['companies-tab'] },
            component: () => import('@/portals/Application/Companies/components/CompaniesList')
          },
          {
            path: '/customers/presentations',
            name: 'app/customers/presentations',
            meta: { requiredTools: ['presentations-tab'] },
            component: () => import('@/portals/Application/Customers/components/Presentations')
          },
          {
            path: '/customers/email/lists',
            name: 'app/customers/email/lists',
            meta: { requiredTools: ['emails-tab'] },
            component: () => import('@/portals/Application/Customers/components/EmailsLists')
          },
          {
            path: '/customers/proofs',
            name: 'app/customers/proofs',
            meta: { requiredTools: ['proofs-tab'] },
            component: () => import('@/portals/Application/Customers/components/Proofs')
          },
          {
            path: '/customers/opportunities',
            name: 'app/customers/opportunities',
            meta: { requiredTools: ['opportunities-tab'] },
            component: () => import('@/components/Opportunities/Opportunities')
          },
          {
            path: '/customers/email/send',
            name: 'app/customers/email/send',
            meta: { requiredTools: ['emails-tab'] },
            component: () => import('@/portals/Application/Customers/components/EmailsLists/Send')
          },
          {
            path: '/customers/email/activity',
            name: 'app/customers/email/activity',
            meta: { requiredTools: ['emails-tab'] },
            component: () => import('@/portals/Application/Customers/components/EmailsActivities')
          },
          {
            path: '/customers/email/templates',
            name: 'app/customers/email/templates',
            meta: { requiredTools: ['emails-tab'] },
            component: () => import('@/portals/Application/Customers/components/EmailTemplates')
          }
        ]
      },
      {
        path: '/orders',
        name: 'app/orders-wrapper',
        redirect: 'orders/',
        component: () => import(/* webpackChunkName: "orders" */ '@/components/MiddleRoute'),
        children: [
          {
            path: ':orderId',
            name: 'app/orders/:orderId',
            component: () => import(/* webpackChunkName: "orders" */ '@/portals/Application/Orders/OrderView')
          }
        ]
      },
      {
        path: '/customers/create',
        component: () => import('@/portals/Application/Customers/CustomerForm')
      },
      {
        path: '/customers/:customerId/edit',
        component: () => import('@/portals/Application/Customers/CustomerForm')
      },

      {
        path: '/customers/:customerId',
        name: 'app/customers/:customerId',
        component: () => import('@/portals/Application/Customers/CustomerViewItem')
      },
      {
        path: '/customers/:customerId/followup',
        name: 'app/customers/:customerId/followup',
        component: () => import('@/portals/Application/Customers/CustomerViewItem')
      },
      {
        path: '/customers/companies/create',
        component: () => import('@/portals/Application/Companies/CompanyForm')
      },
      {
        path: '/customers/companies/:companyId/edit',
        component: () => import('@/portals/Application/Companies/CompanyForm')
      },
      {
        path: '/customers/companies/:companyId',
        component: () => import('@/portals/Application/Companies/CompanyView')
      },
      {
        path: '/customers/presentations/create',
        name: 'app/presentations/create',
        component: () => import('@/portals/Application/Customers/components/Presentations/Form')
      },
      {
        path: '/customers/presentations/:presentationId/edit',
        name: 'app/presentations/edit',
        component: () => import('@/portals/Application/Customers/components/Presentations/Form')
      },
      {
        path: '/customers/presentations/:presentationId/send',
        name: 'app/presentations/send',
        component: () => import('@/portals/Application/Customers/components/Presentations/SendPresentation')
      },
      {
        path: '/customers/email/lists/create',
        name: 'app/email/lists/create',
        component: () => import('@/portals/Application/Customers/components/EmailsLists/Form')
      },
      {
        path: '/customers/email/lists/:emailsList',
        name: 'app/email/lists/view',
        component: () => import('@/portals/Application/Customers/components/EmailsLists/View')
      },
      {
        path: '/customers/email/lists/:emailsList/edit',
        name: 'app/email/lists/edit',
        component: () => import('@/portals/Application/Customers/components/EmailsLists/Form')
      },
      {
        path: '/customers/email/templates/create',
        name: 'app/email/templates/create',
        component: () => import('@/portals/Application/Customers/components/EmailTemplates/Form')
      },
      {
        path: '/customers/email/templates/:emailTemplate/edit',
        name: 'app/email/templates/edit',
        component: () => import('@/portals/Application/Customers/components/EmailTemplates/Form')
      },
      {
        path: '/customers/email/activity/:emailsActivity',
        name: 'app/email/activity/view',
        component: () => import('@/portals/Application/Customers/components/EmailsActivities/View')
      },
      {
        path: '/users',
        name: 'app/users',
        meta: {
          id: ROLES_ACCESS.ACCOUNT_USERS
        },
        component: () => import('@/portals/Application/Users')
      },
      {
        path: 'users/create',
        name: 'app/users/create',
        meta: {
          id: ROLES_ACCESS.ACCOUNT_USERS
        },
        component: () => import('@/components/UserForm')
      },
      {
        path: 'users/:userId/edit',
        name: 'app/users/:userId/edit',

        meta: {
          id: ROLES_ACCESS.ACCOUNT_USERS
        },
        component: () => import('@/components/UserForm')
      },
      {
        path: 'users/:userId/changepassword',
        meta: {
          id: ROLES_ACCESS.ACCOUNT_USERS
        },
        name: 'app/users/:userId/changepassword',
        component: () => import('@/components/ChangePassword')
      },
      {
        path: '/suppliers',
        name: 'app/suppliers',
        component: SuppliersList
      },

      {
        path: 'suppliers/create',
        name: 'suppliers/create',
        component: () => import('@/portals/Administration/Suppliers/SupplierForm'),
        meta: {
          id: [...ROLES_ACCESS.ACCOUNT_USERS, 'suppliers']
        }
      },
      {
        path: 'suppliers/:supplierId/edit',
        name: 'suppliers/:supplierId/edit',
        component: () => import('@/portals/Administration/Suppliers/SupplierForm'),
        meta: { id: ['suppliers'] }
      },
      {
        path: 'suppliers/:supplierId',
        name: 'app/suppliers/:supplierId',
        component: () => import('@/portals/Application/Suppliers/pages/ViewItem'),
        meta: { id: ['suppliers'] }
      },
      {
        path: '/distributors',
        name: 'app/distributors',
        meta: {
          id: ROLES_ACCESS.ACCOUNT_DISTRIBUTOR
        },
        component: () => import('@/portals/Application/Distributors/pages/View')
      },
      {
        path: '/products',
        name: 'app/products',
        component: () => import('@/portals/Application/Products/ProductsSearch/Index')
      },
      {
        path: '/products/:supplierCode/:productId',
        component: ProductView
      },
      {
        path: '/products/:slug',
        component: ProductView
      },
      {
        path: '/stores',
        meta: { requiredTools: [PLAN_TOOLS.STORE[0].value] },
        name: 'app/stores',
        component: () => import(/* webpackChunkName: "orders" */ '@/components/Stores/List')
      },
      {
        path: '/stores/create',
        name: 'app/stores/create',
        component: () => import('@/components/Stores/Form')
      },
      {
        path: '/stores/:storeId',
        name: 'app/stores/:storeId',
        component: () => import('@/components/Stores/Editor/Index')
      },
      {
        path: '/stores/:storeId/products',
        component: () => import('@/components/Stores/v2/View'),
        children: [
          {
            path: 'create',
            component: () => import('@/components/Stores/Editor/Products/form')
          },
          {
            path: ':slug/edit',
            component: () => import('@/components/Stores/Editor/Products/form')
          },

          {
            path: '*',
            redirect: 'create'
          }
        ]
      },
      // {
      //   path: '/stores/:storeId/products/create',
      //   component: () => import('@/components/Stores/Editor/Products/form')
      // },
      // {
      //   path: '/stores/:storeId/products/:slug/edit',
      //   component: () => import('@/components/Stores/Editor/Products/form')
      // },
      {
        path: '/proofs',
        name: 'app/proofs',
        redirect: 'proofs/',
        component: () => import(/* webpackChunkName: "orders" */ '@/components/MiddleRoute'),
        beforeEnter: multiguard([hasAccess]),
        meta: {
          id: ['art', 'proofs', ...ROLES_ACCESS.ARTS]
        },
        children: [
          {
            path: '',
            meta: {
              requiredTools: [PLAN_TOOLS.PROOF[0].value, 'art', 'proofs']
            },
            name: 'app/proofs/',
            component: () => import(/* webpackChunkName: "orders" */ '@/portals/Application/Proofs/Index')
          },
          {
            path: ':proofId',
            name: 'app/proofs/:proofId',
            component: () => import(/* webpackChunkName: "orders" */ '@/portals/Application/Proofs/View')
          }
        ]
      },
      {
        path: '/search',
        name: 'app/search',
        meta: { available: true },
        component: () => import('@/components/search/global/SearchResultsPage'),
        props: { searchPortal: 'app' }
      },
      ...reportsRoutes(),
      ...account('app')
    ].map((route) => mergeGuards(route, [hasAccess]))
  }
];
export { appRoutes as default };
